import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';

import type {
  MergeCountsDto,
  MergeSection,
} from '../api/mergeResolution.types';

import type { HeaderCounts } from '../state/labels';

import SectionHeader from './SectionHeader';

interface SectionAccordionProps {
  section: MergeSection;
  counts: MergeCountsDto;
  /** Passed to the header; see SectionHeader. */
  inputCounts?: { name: string; counts: HeaderCounts };
  /** Passed to the header; see SectionHeader. */
  note?: string[];
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const SectionAccordion = ({
  section,
  counts,
  inputCounts,
  note,
  expanded,
  onToggle,
  children,
}: SectionAccordionProps) => {
  const contentId = `merge-${section}-content`;
  // Fixed aria strings from the CLD-2970 plan; 34T asserts them verbatim.
  const label =
    section === 'auto_resolved'
      ? 'Auto resolved rows'
      : 'Rows needing a decision';

  return (
    <Accordion expanded={expanded} onChange={onToggle}>
      <AccordionSummary
        aria-label={label}
        aria-controls={contentId}
        // Half-height header: MUI's own 48px minimum plus 12px content margins
        // is about double what a one-line title needs.
        sx={{
          minHeight: 32,
          '&.Mui-expanded': { minHeight: 32 },
          // Attribute match: the app remaps MUI's class prefix to `Trinity`.
          '& [class*="AccordionSummary-content"]': { my: 0.5 },
          '& [class*="AccordionSummary-content"].Mui-expanded': { my: 0.5 },
        }}
      >
        <SectionHeader
          section={section}
          counts={counts}
          inputCounts={inputCounts}
          note={note}
        />
      </AccordionSummary>
      <AccordionDetails id={contentId}>{children}</AccordionDetails>
    </Accordion>
  );
};

export default SectionAccordion;
